import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui';
import {
  X,
  Search,
  Check,
  Loader2,
  AlertCircle,
  Store,
  MapPin,
} from 'lucide-react';
import * as merchantsApi from '../../services/merchants.api';
import type { Merchant } from '../../services/merchants.api';
import type { MerchantPickupLocation, ActiveLocationType } from '../../types/merchantPickupLocation';
import MapPicker from './MapPicker';

interface LocationFormModalProps {
  location: MerchantPickupLocation | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (data: {
    merchantId?: number;
    merchantUid?: string;
    merchantName?: string;
    manualLocation?: { lat: number; lng: number; address_text?: string };
    activeType?: ActiveLocationType;
    force?: boolean;
  }) => void;
  isSaving: boolean;
  existingMerchantIds: Set<number>;
  backendError?: string | null;
  requiresConfirmation?: boolean;
}

export default function LocationFormModal({
  location,
  isNew,
  onClose,
  onSave,
  isSaving,
  existingMerchantIds,
  backendError,
  requiresConfirmation,
}: LocationFormModalProps) {
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(
    location
      ? {
          id: location.merchant_id,
          uid: location.merchant_uid,
          full_name: location.merchant_name,
          first_name: '',
          last_name: '',
          email: null,
          phone: null,
          city_id: null,
          created_at: '',
        }
      : null
  );

  const [lat, setLat] = useState<number | null>(location?.manual_location?.lat ?? null);
  const [lng, setLng] = useState<number | null>(location?.manual_location?.lng ?? null);
  const [addressText, setAddressText] = useState(location?.manual_location?.address_text ?? '');
  const [activeType, setActiveType] = useState<ActiveLocationType>(location?.active_type ?? 'manual');
  const [error, setError] = useState<string | null>(null);
  const [showForceConfirm, setShowForceConfirm] = useState(!!requiresConfirmation);

  // Merchant search state
  const [merchantSearch, setMerchantSearch] = useState('');
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: merchants, isLoading: merchantsLoading } = useQuery({
    queryKey: ['merchants', merchantSearch],
    queryFn: () => merchantsApi.getMerchants(merchantSearch || undefined),
    enabled: isNew && showMerchantDropdown,
  });

  const availableMerchants = merchants?.filter((m) => !existingMerchantIds.has(m.id)) ?? [];

  // Show force confirm when backend returns outside_baghdad warning
  const [prevRequiresConfirmation, setPrevRequiresConfirmation] = useState(requiresConfirmation);
  if (requiresConfirmation !== prevRequiresConfirmation) {
    setPrevRequiresConfirmation(requiresConfirmation);
    if (requiresConfirmation) setShowForceConfirm(true);
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMerchantDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationChange = useCallback((newLat: number, newLng: number) => {
    setLat(parseFloat(newLat.toFixed(6)));
    setLng(parseFloat(newLng.toFixed(6)));
    setShowForceConfirm(false);
  }, []);

  const handleSubmit = (e: React.FormEvent, force = false) => {
    e.preventDefault();
    setError(null);

    if (isNew && !selectedMerchant) {
      setError('Please select a merchant');
      return;
    }

    if (activeType === 'manual' && (lat === null || lng === null)) {
      setError('Please set a location on the map or enter lat/lng');
      return;
    }

    // Validate active_type: automatic needs automatic_location to exist
    if (!isNew && activeType === 'automatic' && !location?.automatic_location) {
      setError('Cannot switch to automatic: no automatic location available yet.');
      return;
    }

    const saveData: Parameters<typeof onSave>[0] = {
      activeType,
      force,
    };

    if (isNew && selectedMerchant) {
      saveData.merchantId = selectedMerchant.id;
      saveData.merchantUid = selectedMerchant.uid;
      saveData.merchantName = selectedMerchant.full_name;
    }

    if (lat !== null && lng !== null) {
      saveData.manualLocation = { lat, lng, address_text: addressText };
    }

    onSave(saveData);
  };

  const displayError = backendError || error;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {isNew ? 'Add Merchant Location' : 'Edit Merchant Location'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={(e) => handleSubmit(e)} className="p-6 space-y-4">
          {displayError && !showForceConfirm && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {displayError}
            </div>
          )}

          {showForceConfirm && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
                <AlertCircle className="h-4 w-4" />
                Location Outside Baghdad
              </div>
              <p className="text-sm text-amber-700 mb-3">{backendError}</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowForceConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={(e: React.MouseEvent) => handleSubmit(e as unknown as React.FormEvent, true)}
                >
                  Confirm &amp; Save Anyway
                </Button>
              </div>
            </div>
          )}

          {/* Merchant selector (create only) */}
          {isNew ? (
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Merchant *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={merchantSearch}
                  onChange={(e) => {
                    setMerchantSearch(e.target.value);
                    setSelectedMerchant(null);
                    setShowMerchantDropdown(true);
                  }}
                  onFocus={() => setShowMerchantDropdown(true)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search merchant by name..."
                />
                {selectedMerchant && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                )}
              </div>

              {showMerchantDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {merchantsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      <span className="ml-2 text-sm text-gray-500">Loading merchants...</span>
                    </div>
                  ) : availableMerchants.length === 0 ? (
                    <div className="py-4 px-3 text-sm text-gray-500 text-center">
                      {merchantSearch ? 'No merchants found' : 'Type to search merchants'}
                    </div>
                  ) : (
                    availableMerchants.map((merchant) => (
                      <button
                        key={merchant.id}
                        type="button"
                        onClick={() => {
                          setSelectedMerchant(merchant);
                          setMerchantSearch(merchant.full_name);
                          setShowMerchantDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
                      >
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Store className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{merchant.full_name}</div>
                          <div className="text-xs text-gray-500">ID: {merchant.id} &bull; {merchant.uid}</div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {selectedMerchant && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                  <Store className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700">
                    <strong>{selectedMerchant.full_name}</strong> (ID: {selectedMerchant.id})
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Merchant</label>
              <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Store className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{location?.merchant_name}</div>
                  <div className="text-xs text-gray-500">ID: {location?.merchant_id} &bull; {location?.merchant_uid}</div>
                </div>
              </div>
            </div>
          )}

          {/* Active Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Location Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveType('manual')}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeType === 'manual'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <MapPin className="h-4 w-4 inline mr-1" />
                Manual
              </button>
              <button
                type="button"
                onClick={() => setActiveType('automatic')}
                disabled={isNew || !location?.automatic_location}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeType === 'automatic'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                } ${(isNew || !location?.automatic_location) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Automatic
                {!isNew && !location?.automatic_location && (
                  <span className="block text-xs text-gray-400 mt-0.5">Not available</span>
                )}
              </button>
            </div>
          </div>

          {/* Show automatic location info (read-only) */}
          {!isNew && location?.automatic_location && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-xs font-medium text-green-700 mb-1">Automatic Location (from driver scans)</div>
              <div className="text-sm text-green-800">
                {location.automatic_location.lat.toFixed(6)}, {location.automatic_location.lng.toFixed(6)}
              </div>
              {location.automatic_location.address_text && (
                <div className="text-xs text-green-600 mt-0.5">{location.automatic_location.address_text}</div>
              )}
            </div>
          )}

          {/* Map Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manual Location {activeType === 'manual' ? '*' : ''}
            </label>
            <p className="text-xs text-gray-500 mb-2">Click on the map or drag the marker to set the location. Blue circle shows 100km Baghdad radius.</p>
            <MapPicker lat={lat} lng={lng} onLocationChange={handleLocationChange} height="300px" />
          </div>

          {/* Lat/Lng inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                value={lat ?? ''}
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : null;
                  setLat(val);
                  if (val !== null && lng !== null) handleLocationChange(val, lng);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="33.3152"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                value={lng ?? ''}
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : null;
                  setLng(val);
                  if (lat !== null && val !== null) handleLocationChange(lat, val);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="44.3661"
              />
            </div>
          </div>

          {/* Address text */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Address / Notes</label>
            <input
              type="text"
              value={addressText}
              onChange={(e) => setAddressText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Optional address description..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {isNew ? 'Add Location' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
